import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Film, Calendar, Newspaper, History, Settings, 
  LogOut, Menu, X, Plus, Edit, Trash2, Save, ChevronRight,
  Eye, EyeOff, Upload, Image, Link, Clock, MapPin, Check, AlertCircle,
  FileText, Download, Loader2, FolderOpen, Users, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import AboutManager from './AboutManager';
import PressCodesManager from './PressCodesManager';
import ImagePicker from '../components/admin/ImagePicker';
import { adminFetch as authFetch } from '../services/adminFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

// Sidebar Navigation
const SidebarNav = ({ activeSection, setActiveSection, isOpen, setIsOpen }) => {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'shows', label: 'Shows', icon: Film },
    { id: 'tour-dates', label: 'Tour Dates', icon: Calendar },
    { id: 'media', label: 'Media Assets', icon: FolderOpen },
    { id: 'about', label: 'About Page', icon: Users },
    { id: 'news', label: 'News', icon: Newspaper },
    { id: 'history', label: 'History', icon: History },
    { id: 'privacy', label: 'Privacy Policy', icon: FileText },
    { id: 'press-codes', label: 'Press Access', icon: KeyRound },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#08065E] border-r border-white/10 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <h1 className="text-xl font-bold text-white font-playfair">Admin Panel</h1>
            <p className="text-white/50 text-sm mt-1">UK Family Theatre</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeSection === item.id 
                    ? 'bg-[#D4AF37] text-white' 
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-bold">
                {localStorage.getItem('adminUser')?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">{localStorage.getItem('adminUser') || 'Admin'}</p>
                <p className="text-white/50 text-xs">Administrator</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const DashboardOverview = ({ shows, tourDates, news }) => {
  const stats = [
    { label: 'Total Shows', value: shows.length, icon: Film, color: 'from-purple-500 to-purple-600' },
    { label: 'Tour Dates', value: tourDates.length, icon: Calendar, color: 'from-blue-500 to-blue-600' },
    { label: 'News Articles', value: news.length, icon: Newspaper, color: 'from-green-500 to-green-600' },
    { label: 'Featured Shows', value: shows.filter(s => s.featured).length, icon: Eye, color: 'from-[#D4AF37] to-[#B8960C]' },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white font-playfair">Dashboard Overview</h2>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6"
          >
            <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${stat.color} mb-4`}>
              <stat.icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-3xl font-bold text-white">{stat.value}</p>
            <p className="text-white/60 mt-1">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-left">
            <Plus className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white">Add New Show</span>
          </button>
          <button className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-left">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white">Add Tour Date</span>
          </button>
          <button className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-left">
            <Newspaper className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white">Post News</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Shows Manager
const ShowsManager = ({ shows, onUpdate }) => {
  const [editingShow, setEditingShow] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyShow = {
    id: '',
    title: '',
    shortTitle: '',
    tagline: '',
    description: '',
    status: 'current',
    featured: false,
    poster: '',
    banner: '',
    hero: '',
    videoUrl: '',
    gallery: [],
    ageRange: '3+',
    duration: '90 minutes',
    interval: true,
    partnerLogo: null,
    category: 'family',
    seo: { title: '', description: '' }
  };

  const handleSave = async (show) => {
    setSaving(true);
    try {
      const payload = { ...show, category: show.category || 'family' };
      if (isAdding && !(payload.id || '').trim()) {
        payload.id = (payload.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
        if (!payload.id) {
          toast.error('Please enter a show title first');
          setSaving(false);
          return;
        }
      }
      const endpoint = isAdding ? '/api/admin/shows' : `/api/admin/shows/${payload.id}`;
      const method = isAdding ? 'POST' : 'PUT';
      
      const response = await authFetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(isAdding ? 'Show created successfully!' : 'Show updated successfully!');
        onUpdate();
        setEditingShow(null);
        setIsAdding(false);
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to save show');
      }
    } catch (err) {
      toast.error('Error saving show');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (showId) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/shows/${showId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.ok) {
        toast.success('Show deleted successfully!');
        onUpdate();
      } else {
        toast.error('Failed to delete show');
      }
    } catch (err) {
      toast.error('Error deleting show');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white font-playfair">Manage Shows</h2>
        <button
          onClick={() => { setIsAdding(true); setEditingShow(emptyShow); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] transition-colors"
          data-testid="add-show-button"
        >
          <Plus className="w-5 h-5" />
          Add Show
        </button>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingShow && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a0875] border border-white/10 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8"
            >
              <div className="sticky top-0 bg-[#0a0875] p-6 border-b border-white/10 flex items-center justify-between z-10">
                <h3 className="text-xl font-bold text-white">{isAdding ? 'Add New Show' : 'Edit Show'}</h3>
                <button onClick={() => { setEditingShow(null); setIsAdding(false); }} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Show ID (URL slug)</label>
                    <input
                      type="text"
                      value={editingShow.id}
                      onChange={(e) => setEditingShow({...editingShow, id: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                      data-testid="show-id-input"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="e.g., dinosaur-live"
                      disabled={!isAdding}
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Status</label>
                    <select
                      value={editingShow.status}
                      onChange={(e) => setEditingShow({...editingShow, status: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    >
                      <option value="current">Touring</option>
                      <option value="available">Available</option>
                      <option value="pantomime">Pantomime</option>
                      <option value="past">Past</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Title</label>
                  <input
                    type="text"
                    value={editingShow.title}
                    onChange={(e) => setEditingShow({...editingShow, title: e.target.value})}
                    data-testid="show-title-input"
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    placeholder="Full show title"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Short Title</label>
                    <input
                      type="text"
                      value={editingShow.shortTitle}
                      onChange={(e) => setEditingShow({...editingShow, shortTitle: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Tagline</label>
                    <input
                      type="text"
                      value={editingShow.tagline}
                      onChange={(e) => setEditingShow({...editingShow, tagline: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Description</label>
                  <textarea
                    value={editingShow.description}
                    onChange={(e) => setEditingShow({...editingShow, description: e.target.value})}
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                  />
                </div>

                {/* Show Type — controls which homepage section it appears in */}
                <div className="bg-white/5 border border-[#D4AF37]/30 rounded-xl p-4">
                  <label className="block text-white font-semibold text-sm mb-3">Show Type <span className="text-white/40 font-normal">— which section does this appear in?</span></label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      data-testid="show-type-family"
                      onClick={() => setEditingShow({...editingShow, category: 'family'})}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        (editingShow.category || 'family') === 'family'
                          ? 'bg-[#D4AF37] text-[#020120] border-[#D4AF37]'
                          : 'bg-white/5 text-white/70 border-white/10 hover:border-[#D4AF37]/50'
                      }`}
                    >
                      <span className="block font-bold">Kids / Family Show</span>
                      <span className={`block text-xs mt-1 ${(editingShow.category || 'family') === 'family' ? 'text-[#020120]/70' : 'text-white/40'}`}>
                        Appears in the main family shows section
                      </span>
                    </button>
                    <button
                      type="button"
                      data-testid="show-type-adult"
                      onClick={() => setEditingShow({...editingShow, category: 'big-people'})}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        editingShow.category === 'big-people'
                          ? 'bg-[#D4AF37] text-[#020120] border-[#D4AF37]'
                          : 'bg-white/5 text-white/70 border-white/10 hover:border-[#D4AF37]/50'
                      }`}
                    >
                      <span className="block font-bold">Big People / Adult Show</span>
                      <span className={`block text-xs mt-1 ${editingShow.category === 'big-people' ? 'text-[#020120]/70' : 'text-white/40'}`}>
                        Appears in "The MT Big People Shows" section
                      </span>
                    </button>
                  </div>
                </div>

                {/* Media */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImagePicker
                    label="Poster"
                    value={editingShow.poster}
                    onChange={(url) => setEditingShow({...editingShow, poster: url})}
                    folder="posters"
                    aspect="aspect-[3/4]"
                    testId="show-poster-picker"
                  />
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Video URL (YouTube)</label>
                    <input
                      type="text"
                      value={editingShow.videoUrl || ''}
                      onChange={(e) => setEditingShow({...editingShow, videoUrl: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                </div>

                {/* Banner + Hero images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImagePicker
                    label="Banner (wide image at top of show page)"
                    value={editingShow.banner || ''}
                    onChange={(url) => setEditingShow({...editingShow, banner: url})}
                    folder="banners"
                    aspect="aspect-video"
                    testId="show-banner-picker"
                  />
                  <ImagePicker
                    label="Hero (fallback image)"
                    value={editingShow.hero || ''}
                    onChange={(url) => setEditingShow({...editingShow, hero: url})}
                    folder="heroes"
                    aspect="aspect-video"
                    testId="show-hero-picker"
                  />
                </div>

                {/* Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Age Range</label>
                    <input
                      type="text"
                      value={editingShow.ageRange}
                      onChange={(e) => setEditingShow({...editingShow, ageRange: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="3+"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Duration</label>
                    <input
                      type="text"
                      value={editingShow.duration}
                      onChange={(e) => setEditingShow({...editingShow, duration: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="90 minutes"
                    />
                  </div>
                  <div className="flex items-center gap-4 pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingShow.featured}
                        onChange={(e) => setEditingShow({...editingShow, featured: e.target.checked})}
                        className="w-5 h-5 rounded bg-white/5 border-white/10"
                      />
                      <span className="text-white">Featured</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingShow.interval}
                        onChange={(e) => setEditingShow({...editingShow, interval: e.target.checked})}
                        className="w-5 h-5 rounded bg-white/5 border-white/10"
                      />
                      <span className="text-white">Has Interval</span>
                    </label>
                  </div>
                </div>

                {/* SEO */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-white font-semibold mb-4">SEO Settings</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">SEO Title</label>
                      <input
                        type="text"
                        value={editingShow.seo?.title || ''}
                        onChange={(e) => setEditingShow({...editingShow, seo: {...editingShow.seo, title: e.target.value}})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">SEO Description</label>
                      <textarea
                        value={editingShow.seo?.description || ''}
                        onChange={(e) => setEditingShow({...editingShow, seo: {...editingShow.seo, description: e.target.value}})}
                        rows={2}
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-[#0a0875] p-6 border-t border-white/10 flex justify-end gap-4">
                <button
                  onClick={() => { setEditingShow(null); setIsAdding(false); }}
                  className="px-6 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave(editingShow)}
                  disabled={saving}
                  data-testid="show-save-button"
                  className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] disabled:opacity-50"
                >
                  <Save className="w-5 h-5" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Shows List */}
      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Show</th>
                <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Status</th>
                <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Featured</th>
                <th className="text-right text-white/70 text-sm font-medium px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {shows.filter(s => s.id).map((show) => (
                <tr key={show.id} className="hover:bg-white/5 transition-colors" data-testid={`show-row-${show.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-16 bg-white/10 rounded overflow-hidden flex-shrink-0">
                        {show.poster && (
                          <img src={show.poster.startsWith('http') ? show.poster : `${process.env.PUBLIC_URL || ''}${show.poster}`} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">{show.title}</p>
                        <p className="text-white/50 text-sm">{show.shortTitle}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                      show.status === 'current' ? 'bg-green-500/20 text-green-400' :
                      show.status === 'available' ? 'bg-blue-500/20 text-blue-400' :
                      show.status === 'pantomime' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {show.status}
                    </span>
                    <span className={`ml-2 inline-flex px-2 py-1 rounded-full text-[10px] font-semibold ${
                      show.category === 'big-people' ? 'bg-purple-500/20 text-purple-300' : 'bg-sky-500/20 text-sky-300'
                    }`} data-testid={`show-category-badge-${show.id}`}>
                      {show.category === 'big-people' ? 'Big People' : 'Family'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {show.featured ? (
                      <Check className="w-5 h-5 text-[#D4AF37]" />
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditingShow(show)}
                        className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        data-testid={`edit-show-${show.id}`}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(show.id)}
                        className="p-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-show-${show.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Tour Dates Manager
const TourDatesManager = ({ tourDates, shows, onUpdate }) => {
  const [editingDate, setEditingDate] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkImportShow, setBulkImportShow] = useState('');
  const [bulkImportPreview, setBulkImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const emptyTourDate = {
    id: '',
    showId: '',
    venue: '',
    town: '',
    postcode: '',
    lat: null,
    lng: null,
    date: '',
    time: '',
    ticketUrl: '',
    status: 'available'
  };

  const handleSave = async (tourDate) => {
    setSaving(true);
    try {
      const endpoint = isAdding ? '/api/admin/tour-dates' : `/api/admin/tour-dates/${tourDate.id}`;
      const method = isAdding ? 'POST' : 'PUT';
      
      const response = await authFetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(tourDate)
      });

      if (response.ok) {
        toast.success(isAdding ? 'Tour date created!' : 'Tour date updated!');
        onUpdate();
        setEditingDate(null);
        setIsAdding(false);
      } else {
        toast.error('Failed to save tour date');
      }
    } catch (err) {
      toast.error('Error saving tour date');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dateId) => {
    if (!window.confirm('Are you sure you want to delete this tour date?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/tour-dates/${dateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.ok) {
        toast.success('Tour date deleted!');
        onUpdate();
      }
    } catch (err) {
      toast.error('Error deleting tour date');
    }
  };

  // Parse CSV/Text data for bulk import
  const parseBulkData = (text) => {
    const lines = text.trim().split('\n').filter(line => line.trim());
    const parsed = [];
    
    for (const line of lines) {
      // Try to parse as CSV (Date, Venue, Town, Time, Ticket URL)
      // Or as tab-separated
      const parts = line.includes('\t') ? line.split('\t') : line.split(',');
      
      if (parts.length >= 3) {
        const [date, venue, town, time, ticketUrl, postcode] = parts.map(p => p.trim());
        
        // Try to parse date in various formats
        let parsedDate = date;
        if (date.includes('/')) {
          const [day, month, year] = date.split('/');
          parsedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        parsed.push({
          id: `td-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          showId: bulkImportShow,
          date: parsedDate,
          venue: venue || '',
          town: town || '',
          time: time || '',
          ticketUrl: ticketUrl || '',
          postcode: postcode || '',
          status: 'available'
        });
      }
    }
    
    return parsed;
  };

  const handlePreviewImport = () => {
    const parsed = parseBulkData(bulkImportData);
    setBulkImportPreview(parsed);
  };

  const handleBulkImport = async () => {
    if (bulkImportPreview.length === 0) {
      toast.error('No valid dates to import');
      return;
    }

    setImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const tourDate of bulkImportPreview) {
      try {
        const response = await authFetch(`${API_URL}/api/admin/tour-dates`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify(tourDate)
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        errorCount++;
      }
    }

    setImporting(false);
    
    if (successCount > 0) {
      toast.success(`Imported ${successCount} tour dates!`);
      onUpdate();
    }
    if (errorCount > 0) {
      toast.error(`Failed to import ${errorCount} dates`);
    }

    setShowBulkImport(false);
    setBulkImportData('');
    setBulkImportShow('');
    setBulkImportPreview([]);
  };

  const downloadTemplate = () => {
    const template = `Date,Venue,Town,Time,Ticket URL,Postcode
25/03/2026,Theatre Royal,Norwich,14:00,https://tickets.example.com/123,NR1 3PY
26/03/2026,Playhouse,Edinburgh,19:30,https://tickets.example.com/456,EH1 2LJ
27/03/2026,Grand Theatre,Leeds,14:00,https://tickets.example.com/789,LS1 6NU`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tour_dates_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-white font-playfair">Manage Tour Dates</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowBulkImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors border border-white/20"
            data-testid="bulk-import-button"
          >
            <Upload className="w-5 h-5" />
            Bulk Import
          </button>
          <button
            onClick={() => { setIsAdding(true); setEditingDate(emptyTourDate); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] transition-colors"
            data-testid="add-tour-date-button"
          >
            <Plus className="w-5 h-5" />
            Add Tour Date
          </button>
        </div>
      </div>

      {/* Bulk Import Modal */}
      <AnimatePresence>
        {showBulkImport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#0a0875] border border-white/10 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white">Bulk Import Tour Dates</h3>
                  <p className="text-white/50 text-sm mt-1">Import multiple dates from CSV or text</p>
                </div>
                <button onClick={() => { setShowBulkImport(false); setBulkImportPreview([]); }} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Show Selection */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">Select Show *</label>
                  <select
                    value={bulkImportShow}
                    onChange={(e) => setBulkImportShow(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white"
                  >
                    <option value="">Choose a show...</option>
                    {shows.map(show => (
                      <option key={show.id} value={show.id}>{show.title}</option>
                    ))}
                  </select>
                </div>

                {/* Template Download */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-white text-sm font-medium">Download CSV Template</p>
                      <p className="text-white/50 text-xs">Use this format for best results</p>
                    </div>
                  </div>
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>

                {/* Data Input */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    Paste CSV or Tab-Separated Data
                  </label>
                  <textarea
                    value={bulkImportData}
                    onChange={(e) => setBulkImportData(e.target.value)}
                    placeholder={`Date,Venue,Town,Time,Ticket URL,Postcode
25/03/2026,Theatre Royal,Norwich,14:00,https://tickets.com/123,NR1 3PY
26/03/2026,Playhouse,Edinburgh,19:30,https://tickets.com/456,EH1 2LJ`}
                    rows={6}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white font-mono text-sm placeholder-white/30"
                  />
                  <p className="text-white/40 text-xs mt-2">
                    Format: Date, Venue, Town, Time (optional), Ticket URL (optional), Postcode (optional)
                  </p>
                </div>

                {/* Preview Button */}
                <button
                  onClick={handlePreviewImport}
                  disabled={!bulkImportShow || !bulkImportData.trim()}
                  className="w-full py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Preview Import ({parseBulkData(bulkImportData).length} dates detected)
                </button>

                {/* Preview Table */}
                {bulkImportPreview.length > 0 && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <div className="p-3 bg-green-500/20 border-b border-white/10">
                      <p className="text-green-400 text-sm font-medium flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        {bulkImportPreview.length} dates ready to import
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-white/5 sticky top-0">
                          <tr>
                            <th className="text-left text-white/70 px-4 py-2">Date</th>
                            <th className="text-left text-white/70 px-4 py-2">Venue</th>
                            <th className="text-left text-white/70 px-4 py-2">Town</th>
                            <th className="text-left text-white/70 px-4 py-2">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {bulkImportPreview.map((item, idx) => (
                            <tr key={idx}>
                              <td className="text-white px-4 py-2">{item.date}</td>
                              <td className="text-white px-4 py-2">{item.venue}</td>
                              <td className="text-white/70 px-4 py-2">{item.town}</td>
                              <td className="text-white/70 px-4 py-2">{item.time || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-6 border-t border-white/10 flex justify-end gap-4">
                <button
                  onClick={() => { setShowBulkImport(false); setBulkImportPreview([]); }}
                  className="px-6 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={importing || bulkImportPreview.length === 0}
                  className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] disabled:opacity-50"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Import {bulkImportPreview.length} Dates
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#0a0875] border border-white/10 rounded-xl w-full max-w-2xl"
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">{isAdding ? 'Add Tour Date' : 'Edit Tour Date'}</h3>
                <button onClick={() => { setEditingDate(null); setIsAdding(false); }} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Show</label>
                  <select
                    value={editingDate.showId}
                    onChange={(e) => setEditingDate({...editingDate, showId: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                  >
                    <option value="">Select a show...</option>
                    {shows.map(show => (
                      <option key={show.id} value={show.id}>{show.title}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Venue</label>
                    <input
                      type="text"
                      value={editingDate.venue}
                      onChange={(e) => setEditingDate({...editingDate, venue: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="Theatre Royal"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Town</label>
                    <input
                      type="text"
                      value={editingDate.town}
                      onChange={(e) => setEditingDate({...editingDate, town: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="Norwich"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Postcode</label>
                    <input
                      type="text"
                      value={editingDate.postcode}
                      onChange={(e) => setEditingDate({...editingDate, postcode: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      placeholder="NR2 1RL"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Date</label>
                    <input
                      type="date"
                      value={editingDate.date}
                      onChange={(e) => setEditingDate({...editingDate, date: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Time</label>
                    <input
                      type="time"
                      value={editingDate.time}
                      onChange={(e) => setEditingDate({...editingDate, time: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Ticket URL</label>
                  <input
                    type="url"
                    value={editingDate.ticketUrl}
                    onChange={(e) => setEditingDate({...editingDate, ticketUrl: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end gap-4">
                <button
                  onClick={() => { setEditingDate(null); setIsAdding(false); }}
                  className="px-6 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave(editingDate)}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] disabled:opacity-50"
                >
                  <Save className="w-5 h-5" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tour Dates List */}
      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <input
            type="text"
            placeholder="Search by show, venue, town, or date (YYYY-MM-DD)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="tour-dates-search"
            className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]"
          />
          {searchTerm && (
            <p className="text-white/50 text-xs mt-2">
              Showing {tourDates.filter(td => {
                const t = searchTerm.toLowerCase();
                const s = shows.find(x => x.id === td.showId);
                return (s?.title || '').toLowerCase().includes(t)
                  || (s?.shortTitle || '').toLowerCase().includes(t)
                  || (td.venue || '').toLowerCase().includes(t)
                  || (td.town || '').toLowerCase().includes(t)
                  || (td.date || '').includes(t);
              }).length} of {tourDates.length} dates
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Show</th>
                <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Venue</th>
                <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Date</th>
                <th className="text-right text-white/70 text-sm font-medium px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {tourDates
                .slice()
                .sort((a, b) => {
                  // Smart sort: upcoming dates first (ascending), then past dates (descending most-recent first)
                  const today = new Date().toISOString().slice(0, 10);
                  const aFuture = (a.date || '') >= today;
                  const bFuture = (b.date || '') >= today;
                  if (aFuture && !bFuture) return -1;
                  if (!aFuture && bFuture) return 1;
                  if (aFuture) {
                    // Both future: earliest first
                    return (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || '');
                  }
                  // Both past: most recent first
                  return (b.date || '').localeCompare(a.date || '') || (b.time || '').localeCompare(a.time || '');
                })
                .filter((td) => {
                  if (!searchTerm) return true;
                  const t = searchTerm.toLowerCase();
                  const s = shows.find(x => x.id === td.showId);
                  return (s?.title || '').toLowerCase().includes(t)
                    || (s?.shortTitle || '').toLowerCase().includes(t)
                    || (td.venue || '').toLowerCase().includes(t)
                    || (td.town || '').toLowerCase().includes(t)
                    || (td.date || '').includes(t);
                })
                .map((td) => {
                const show = shows.find(s => s.id === td.showId);
                return (
                  <tr key={td.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-white">{show?.shortTitle || td.showId}</td>
                    <td className="px-6 py-4">
                      <p className="text-white">{td.venue}</p>
                      <p className="text-white/50 text-sm">{td.town}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white">{td.date}</p>
                      <p className="text-white/50 text-sm">{td.time}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingDate(td)}
                          className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(td.id)}
                          className="p-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// News Manager
const NewsManager = ({ news, onUpdate }) => {
  const [editingArticle, setEditingArticle] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const categories = [
    { value: 'announcement', label: 'Announcement' },
    { value: 'press', label: 'Press' },
    { value: 'company', label: 'Company' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'behind-scenes', label: 'Behind the Scenes' },
    { value: 'milestone', label: 'Milestone' },
    { value: 'event', label: 'Event' },
    { value: 'international', label: 'International' },
  ];

  const emptyArticle = {
    id: '',
    title: '',
    excerpt: '',
    content: '',
    image: '',
    date: new Date().toISOString().split('T')[0],
    category: 'announcement',
    featured: false
  };

  const handleSave = async (article) => {
    if (!article.title.trim()) { toast.error('Title is required'); return; }
    if (!article.excerpt.trim()) { toast.error('Excerpt is required'); return; }
    setSaving(true);
    try {
      const endpoint = isAdding ? '/api/admin/news' : `/api/admin/news/${article.id}`;
      const method = isAdding ? 'POST' : 'PUT';
      
      const response = await authFetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(article)
      });

      if (response.ok) {
        toast.success(isAdding ? 'Article created!' : 'Article updated!');
        onUpdate();
        setEditingArticle(null);
        setIsAdding(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save article');
      }
    } catch (err) {
      toast.error('Error saving article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      const response = await authFetch(`${API_URL}/api/admin/news/${articleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
      });
      if (response.ok) {
        toast.success('Article deleted!');
        onUpdate();
      } else {
        toast.error('Failed to delete article');
      }
    } catch (err) {
      toast.error('Error deleting article');
    }
  };

  const filteredNews = news
    .filter(a => filterCategory === 'all' || a.category === filterCategory)
    .filter(a => !searchTerm || a.title.toLowerCase().includes(searchTerm.toLowerCase()) || a.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const categoryColor = (cat) => {
    const colors = {
      announcement: 'bg-blue-500/20 text-blue-400',
      press: 'bg-purple-500/20 text-purple-400',
      company: 'bg-green-500/20 text-green-400',
      partnership: 'bg-yellow-500/20 text-yellow-400',
      'behind-scenes': 'bg-pink-500/20 text-pink-400',
      milestone: 'bg-[#D4AF37]/20 text-[#D4AF37]',
      event: 'bg-orange-500/20 text-orange-400',
      international: 'bg-teal-500/20 text-teal-400',
    };
    return colors[cat] || 'bg-white/10 text-white/60';
  };

  return (
    <div className="space-y-6" data-testid="news-manager">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white font-playfair">Manage News</h2>
          <p className="text-white/50 text-sm mt-1">{news.length} articles</p>
        </div>
        <button
          onClick={() => { setIsAdding(true); setEditingArticle(emptyArticle); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] transition-colors"
          data-testid="add-news-button"
        >
          <Plus className="w-5 h-5" />
          Add Article
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search articles..."
            className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 pl-10 pr-4 text-white text-sm placeholder-white/30"
            data-testid="news-search-input"
          />
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg py-2.5 px-4 text-white text-sm min-w-[160px]"
          data-testid="news-category-filter"
        >
          <option value="all">All Categories</option>
          {categories.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Edit/Add Modal */}
      <AnimatePresence>
        {editingArticle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a0875] border border-white/10 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8"
            >
              <div className="sticky top-0 bg-[#0a0875] p-6 border-b border-white/10 flex items-center justify-between z-10">
                <h3 className="text-xl font-bold text-white">{isAdding ? 'Add New Article' : 'Edit Article'}</h3>
                <button onClick={() => { setEditingArticle(null); setIsAdding(false); }} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Title *</label>
                  <input
                    type="text"
                    value={editingArticle.title}
                    onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    placeholder="Article headline"
                    data-testid="news-edit-title"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Excerpt *</label>
                  <textarea
                    value={editingArticle.excerpt}
                    onChange={(e) => setEditingArticle({...editingArticle, excerpt: e.target.value})}
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    placeholder="Short summary (shown on cards)"
                    data-testid="news-edit-excerpt"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Full Content</label>
                  <textarea
                    value={editingArticle.content}
                    onChange={(e) => setEditingArticle({...editingArticle, content: e.target.value})}
                    rows={6}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                    placeholder="Full article body text"
                    data-testid="news-edit-content"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImagePicker
                    label="Article Image"
                    value={editingArticle.image}
                    onChange={(url) => setEditingArticle({...editingArticle, image: url})}
                    folder="news"
                    aspect="aspect-video"
                    testId="news-image-picker"
                  />
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Date</label>
                    <input
                      type="date"
                      value={editingArticle.date}
                      onChange={(e) => setEditingArticle({...editingArticle, date: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      data-testid="news-edit-date"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Category</label>
                    <select
                      value={editingArticle.category}
                      onChange={(e) => setEditingArticle({...editingArticle, category: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-4 text-white"
                      data-testid="news-edit-category"
                    >
                      {categories.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center pt-7">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingArticle.featured}
                        onChange={(e) => setEditingArticle({...editingArticle, featured: e.target.checked})}
                        className="w-5 h-5 rounded bg-white/5 border-white/10"
                        data-testid="news-edit-featured"
                      />
                      <span className="text-white">Featured Article</span>
                    </label>
                  </div>
                </div>

              </div>

              <div className="sticky bottom-0 bg-[#0a0875] p-6 border-t border-white/10 flex justify-end gap-4">
                <button
                  onClick={() => { setEditingArticle(null); setIsAdding(false); }}
                  className="px-6 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave(editingArticle)}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] disabled:opacity-50"
                  data-testid="news-save-button"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {saving ? 'Saving...' : 'Save Article'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Articles List */}
      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
        {filteredNews.length === 0 ? (
          <div className="text-center py-12">
            <Newspaper className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">
              {searchTerm || filterCategory !== 'all' ? 'No articles match your filters' : 'No news articles yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Article</th>
                  <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Category</th>
                  <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Date</th>
                  <th className="text-left text-white/70 text-sm font-medium px-6 py-4">Featured</th>
                  <th className="text-right text-white/70 text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredNews.map((article) => (
                  <tr key={article.id} className="hover:bg-white/5 transition-colors" data-testid={`news-row-${article.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-12 bg-white/10 rounded-lg overflow-hidden flex-shrink-0">
                          {article.image && (
                            <img
                              src={article.image.startsWith('http') ? article.image : `${API_URL}${article.image}`}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate max-w-xs">{article.title}</p>
                          <p className="text-white/40 text-sm truncate max-w-xs">{article.excerpt}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium capitalize ${categoryColor(article.category)}`}>
                        {(article.category || '').replace(/-/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white/70 text-sm">
                        {new Date(article.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {article.featured ? (
                        <Check className="w-5 h-5 text-[#D4AF37]" />
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingArticle({...article})}
                          className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          data-testid={`edit-news-${article.id}`}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="p-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          data-testid={`delete-news-${article.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// History Manager
const HistoryManager = ({ onUpdate }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingYear, setEditingYear] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/history`);
      if (res.ok) {
        const data = await res.json();
        setTimeline(data.timeline || []);
      }
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const saveYear = async (yearData, isNew = false) => {
    setSaving(true);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/history`
        : `${API_URL}/api/admin/history/${yearData.year}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
        body: JSON.stringify(yearData),
      });
      if (res.ok) {
        toast.success(isNew ? 'Year added' : 'Year updated');
        setEditingYear(null);
        fetchHistory();
      } else {
        toast.error('Failed to save');
      }
    } catch (err) {
      toast.error('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const deleteYear = async (year) => {
    if (!window.confirm(`Delete year ${year}?`)) return;
    try {
      const res = await authFetch(`${API_URL}/api/admin/history/${year}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
      });
      if (res.ok) {
        toast.success(`Year ${year} deleted`);
        fetchHistory();
      }
    } catch (err) {
      toast.error('Error deleting');
    }
  };

  if (loading) return <div className="text-white/60 text-center py-12">Loading...</div>;

  return (
    <div data-testid="history-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white">History Timeline</h2>
          <p className="text-white/50 mt-1">{timeline.length} years</p>
        </div>
        <button
          onClick={() => setEditingYear({ year: new Date().getFullYear(), events: [''], highlight: false, image: '', _isNew: true })}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#C4A030]"
          data-testid="add-history-year-btn"
        >
          <Plus className="w-4 h-4" /> Add Year
        </button>
      </div>

      <div className="space-y-3">
        {timeline.sort((a, b) => b.year - a.year).map(entry => (
          <div key={entry.year} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-start gap-4">
              {entry.image && (
                <img
                  src={entry.image}
                  alt={`Year ${entry.year}`}
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-white/10"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-white font-bold text-lg">{entry.year}</h4>
                  {entry.highlight && (
                    <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full">Highlight</span>
                  )}
                </div>
                <ul className="space-y-1">
                  {(entry.events || []).map((event, i) => (
                    <li key={i} className="text-white/50 text-sm flex items-start gap-2">
                      <span className="text-[#D4AF37] mt-1 flex-shrink-0">&#8226;</span>
                      <span>{event}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditingYear({ ...entry })}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
                  data-testid={`edit-year-${entry.year}`}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteYear(entry.year)}
                  className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-400/10 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Add Year Modal */}
      {editingYear && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingYear(null)}>
          <div
            className="bg-[#0a0875] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0a0875] border-b border-white/10 p-4 flex justify-between items-center z-10">
              <h3 className="text-white font-bold">{editingYear._isNew ? 'Add' : 'Edit'} Year</h3>
              <button onClick={() => setEditingYear(null)} className="p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-sm mb-1 block">Year</label>
                  <input
                    type="number"
                    value={editingYear.year}
                    onChange={e => setEditingYear({ ...editingYear, year: parseInt(e.target.value) || 0 })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    disabled={!editingYear._isNew}
                  />
                </div>
                <ImagePicker
                  label="Year Image"
                  value={editingYear.image || ''}
                  onChange={(url) => setEditingYear({ ...editingYear, image: url })}
                  folder="timeline"
                  aspect="aspect-video"
                  testId="timeline-image-picker"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editingYear.highlight || false}
                  onChange={e => setEditingYear({ ...editingYear, highlight: e.target.checked })}
                  className="rounded"
                  id="highlight-check"
                />
                <label htmlFor="highlight-check" className="text-white/60 text-sm">Highlight year (featured in timeline)</label>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-sm">Events</label>
                  <button
                    onClick={() => setEditingYear({ ...editingYear, events: [...(editingYear.events || []), ''] })}
                    className="text-[#D4AF37] text-xs hover:underline"
                  >
                    + Add event
                  </button>
                </div>
                <div className="space-y-2">
                  {(editingYear.events || []).map((event, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={event}
                        onChange={e => {
                          const newEvents = [...editingYear.events];
                          newEvents[i] = e.target.value;
                          setEditingYear({ ...editingYear, events: newEvents });
                        }}
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        placeholder="Event description"
                      />
                      <button
                        onClick={() => {
                          const newEvents = editingYear.events.filter((_, idx) => idx !== i);
                          setEditingYear({ ...editingYear, events: newEvents });
                        }}
                        className="p-2 text-red-400/60 hover:text-red-400 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setEditingYear(null)} className="px-4 py-2 text-white/60 border border-white/10 rounded-lg">Cancel</button>
                <button
                  onClick={() => {
                    const { _isNew, ...data } = editingYear;
                    saveYear(data, _isNew);
                  }}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg disabled:opacity-50"
                  data-testid="save-year-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Media Manager - Upload assets to R2 with Drag & Drop
const MEDIA_CATEGORIES = [
  { id: 'Gallery', label: 'Gallery', hint: 'Shown on website + press' },
  { id: 'Promo', label: 'Promo Video', hint: 'Main video on the show page' },
  { id: 'AdditionalVideos', label: 'Additional Videos', hint: 'Press only – bespoke trailers' },
  { id: 'SocialVideos', label: 'Social Videos', hint: 'Press only – reels & socials' },
  { id: 'Posters', label: 'Posters', hint: 'Press only' },
  { id: 'Banners', label: 'Banners', hint: 'Press only' },
  { id: 'Documents', label: 'Documents', hint: 'Press only' },
];
const CATEGORY_LABELS = Object.fromEntries(MEDIA_CATEGORIES.map(c => [c.id, c.label]));

const MediaManager = ({ shows }) => {
  const [selectedShow, setSelectedShow] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = React.useRef(null);
  const dropZoneRef = React.useRef(null);

  const fetchAssets = async (showId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/media/assets/${showId}`);
      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleShowSelect = (show) => {
    setSelectedShow(show);
    fetchAssets(show.id);
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  };

  // Process selected/dropped files
  const processFiles = (files) => {
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') || file.type.startsWith('video/') || 
                      file.type === 'application/pdf' || file.name.endsWith('.doc') || file.name.endsWith('.docx');
      if (!isValid) {
        toast.error(`Invalid file type: ${file.name}`);
      }
      return isValid;
    });

    if (validFiles.length > 0) {
      // Create preview URLs for images
      const filesWithPreview = validFiles.map(file => ({
        file,
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        status: 'pending', // pending, uploading, success, error
        progress: 0
      }));
      setPendingFiles(filesWithPreview);
      setShowUploadModal(true);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processFiles(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (fileId) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== fileId);
    });
  };

  const clearAllPendingFiles = () => {
    pendingFiles.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setPendingFiles([]);
    setShowUploadModal(false);
  };

  // Upload all pending files
  const uploadAllFiles = async () => {
    if (!selectedShow || pendingFiles.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: pendingFiles.length, percent: 0 });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < pendingFiles.length; i++) {
      const fileObj = pendingFiles[i];
      
      // Update status to uploading
      setPendingFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'uploading' } : f
      ));

      try {
        const formData = new FormData();
        formData.append('file', fileObj.file);
        formData.append('category', uploadCategory);

        const response = await authFetch(`${API_URL}/api/admin/media/upload/${selectedShow.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: formData
        });

        if (response.ok) {
          successCount++;
          try {
            const data = await response.json();
            if (data.promoVideoSet) {
              toast.success(`Promo video is now live on the ${selectedShow.shortTitle || selectedShow.title} show page`);
            }
          } catch (e) { /* non-JSON response, ignore */ }
          setPendingFiles(prev => prev.map(f => 
            f.id === fileObj.id ? { ...f, status: 'success', progress: 100 } : f
          ));
        } else {
          errorCount++;
          const err = await response.json();
          setPendingFiles(prev => prev.map(f => 
            f.id === fileObj.id ? { ...f, status: 'error', errorMsg: err.detail || 'Upload failed' } : f
          ));
        }
      } catch (err) {
        errorCount++;
        setPendingFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: 'error', errorMsg: 'Network error' } : f
        ));
      }

      setUploadProgress({
        current: i + 1,
        total: pendingFiles.length,
        percent: Math.round(((i + 1) / pendingFiles.length) * 100)
      });
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s) successfully!`);
      fetchAssets(selectedShow.id);
    }
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} file(s)`);
    }

    // Auto-close modal after a short delay if all succeeded
    if (errorCount === 0) {
      setTimeout(() => {
        clearAllPendingFiles();
      }, 1500);
    }
  };

  const handleDeleteAsset = async (asset) => {
    if (!window.confirm(`Delete ${asset.displayName || asset.name}?`)) return;

    try {
      const encodedPath = asset.name.split('/').map(encodeURIComponent).join('/');
      const response = await authFetch(`${API_URL}/api/admin/media/${selectedShow.id}/${encodedPath}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.ok) {
        toast.success('File deleted');
        fetchAssets(selectedShow.id);
      } else {
        toast.error('Failed to delete file');
      }
    } catch (err) {
      toast.error('Error deleting file');
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white font-playfair">Media Assets</h2>
          <p className="text-white/50 text-sm mt-1">Upload and manage assets for each show</p>
        </div>
      </div>

      {!selectedShow ? (
        // Show selection grid
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {shows.map((show) => (
            <button
              key={show.id}
              onClick={() => handleShowSelect(show)}
              className="group text-left bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/50 hover:bg-white/10 transition-all"
            >
              <div className="aspect-[3/4] bg-white/5 relative overflow-hidden">
                {show.poster && (
                  <img 
                    src={show.poster.startsWith('http') ? show.poster : `${process.env.PUBLIC_URL || ''}${show.poster}`}
                    alt={show.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-semibold text-sm truncate">{show.shortTitle || show.title}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        // Asset management view
        <div className="space-y-6">
          {/* Header with back button */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setSelectedShow(null); setAssets([]); }}
              className="text-white/50 hover:text-white transition-colors"
            >
              ← Back to shows
            </button>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white">{selectedShow.title}</h3>
              <p className="text-white/50 text-sm">{assets.length} files</p>
            </div>
          </div>

          {/* Upload Category Picker */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/70 text-sm font-semibold mb-3">Step 1 — choose where to upload:</p>
            <div className="flex flex-wrap gap-2">
              {MEDIA_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`media-category-${cat.id.toLowerCase()}`}
                  onClick={() => setUploadCategory(cat.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                    uploadCategory === cat.id
                      ? 'bg-[#D4AF37] text-[#020120] border-[#D4AF37]'
                      : 'bg-white/5 text-white/70 border-white/10 hover:border-[#D4AF37]/50'
                  }`}
                >
                  {cat.label}
                  <span className={`block text-[10px] font-normal ${uploadCategory === cat.id ? 'text-[#020120]/70' : 'text-white/40'}`}>
                    {cat.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Drag & Drop Upload Zone — only visible once a category is chosen */}
          {!uploadCategory ? (
            <div
              data-testid="upload-category-prompt"
              className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center bg-white/[0.02]"
            >
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-white/25" />
              </div>
              <h4 className="text-white/60 font-semibold text-lg mb-1">Choose a category above to upload</h4>
              <p className="text-white/35 text-sm">This makes sure files land in the right place (website gallery vs press-only)</p>
            </div>
          ) : (
          <div
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-300 ${
              isDragging 
                ? 'border-[#D4AF37] bg-[#D4AF37]/20 scale-[1.02]' 
                : 'border-white/20 bg-white/5 hover:border-[#D4AF37]/50 hover:bg-white/10'
            }`}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all ${
                isDragging ? 'bg-[#D4AF37] scale-110' : 'bg-[#D4AF37]/20'
              }`}>
                <Upload className={`w-8 h-8 ${isDragging ? 'text-white' : 'text-[#D4AF37]'}`} />
              </div>
              <h4 className="text-white font-semibold text-lg mb-2">
                {isDragging ? 'Drop files here!' : 'Drag & Drop Files'}
              </h4>
              <p className="text-white/50 text-sm mb-4">
                or click to browse your computer
              </p>
              <p className="text-white/30 text-xs mb-6">
                Supports: Images, Videos, PDFs, Documents
              </p>
              <label className="cursor-pointer px-6 py-3 bg-[#D4AF37] text-[#020120] rounded-xl font-semibold hover:bg-white transition-colors flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Browse Files
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
          </div>
          )}

          {/* Bulk Upload Modal */}
          <AnimatePresence>
            {showUploadModal && pendingFiles.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                onClick={(e) => { if (e.target === e.currentTarget && !uploading) clearAllPendingFiles(); }}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-[#0a0875] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
                >
                  {/* Modal Header */}
                  <div className="p-5 border-b border-white/10 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">Upload {pendingFiles.length} File{pendingFiles.length > 1 ? 's' : ''}</h3>
                      <p className="text-white/50 text-sm mt-1">to {selectedShow.shortTitle || selectedShow.title} → <span className="text-[#D4AF37] font-semibold">{CATEGORY_LABELS[uploadCategory] || uploadCategory}</span></p>
                    </div>
                    {!uploading && (
                      <button onClick={clearAllPendingFiles} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-white/60" />
                      </button>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {uploading && (
                    <div className="px-5 py-3 bg-[#D4AF37]/10 border-b border-white/10">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-white/70">Uploading {uploadProgress.current} of {uploadProgress.total}</span>
                        <span className="text-[#D4AF37] font-semibold">{uploadProgress.percent}%</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-[#D4AF37]"
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadProgress.percent}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    </div>
                  )}

                  {/* File List */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-3">
                    {pendingFiles.map((fileObj) => (
                      <div
                        key={fileObj.id}
                        className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                          fileObj.status === 'success' ? 'bg-green-500/10 border-green-500/30' :
                          fileObj.status === 'error' ? 'bg-red-500/10 border-red-500/30' :
                          fileObj.status === 'uploading' ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' :
                          'bg-white/5 border-white/10'
                        }`}
                      >
                        {/* Thumbnail */}
                        <div className="w-14 h-14 rounded-lg bg-white/10 flex-shrink-0 overflow-hidden flex items-center justify-center">
                          {fileObj.preview ? (
                            <img src={fileObj.preview} alt="" className="w-full h-full object-cover" />
                          ) : fileObj.file.type.startsWith('video/') ? (
                            <Film className="w-6 h-6 text-white/40" />
                          ) : (
                            <FileText className="w-6 h-6 text-white/40" />
                          )}
                        </div>

                        {/* File Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{fileObj.file.name}</p>
                          <p className="text-white/50 text-xs mt-0.5">{formatSize(fileObj.file.size)}</p>
                          {fileObj.status === 'error' && (
                            <p className="text-red-400 text-xs mt-1">{fileObj.errorMsg}</p>
                          )}
                        </div>

                        {/* Status Icon */}
                        <div className="flex-shrink-0">
                          {fileObj.status === 'uploading' && (
                            <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                          )}
                          {fileObj.status === 'success' && (
                            <Check className="w-5 h-5 text-green-500" />
                          )}
                          {fileObj.status === 'error' && (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          )}
                          {fileObj.status === 'pending' && !uploading && (
                            <button
                              onClick={() => removePendingFile(fileObj.id)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                            >
                              <X className="w-4 h-4 text-white/40" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Modal Footer */}
                  <div className="p-5 border-t border-white/10 flex items-center justify-between gap-4">
                    <p className="text-white/40 text-sm">
                      Total: {formatSize(pendingFiles.reduce((sum, f) => sum + f.file.size, 0))}
                    </p>
                    <div className="flex items-center gap-3">
                      {!uploading && (
                        <button
                          onClick={clearAllPendingFiles}
                          className="px-5 py-2.5 text-white/60 border border-white/20 rounded-xl hover:bg-white/5 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={uploadAllFiles}
                        disabled={uploading || pendingFiles.every(f => f.status === 'success')}
                        className="px-6 py-2.5 bg-[#D4AF37] text-[#020120] rounded-xl font-semibold hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Uploading...
                          </>
                        ) : pendingFiles.every(f => f.status === 'success') ? (
                          <>
                            <Check className="w-5 h-5" />
                            Done!
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            Upload All
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Assets list */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
              <FolderOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No assets uploaded yet</p>
              <p className="text-white/30 text-sm mt-1">Drag & drop files above to get started</p>
            </div>
          ) : (
            <>
              {/* Category filter tabs */}
              <div className="flex flex-wrap gap-2">
                {['All', ...MEDIA_CATEGORIES.map(c => c.id), 'Other'].map((cat) => {
                  const count = cat === 'All' ? assets.length : assets.filter(a => (a.category || 'Other') === cat).length;
                  if (cat === 'Other' && count === 0) return null;
                  return (
                    <button
                      key={cat}
                      data-testid={`media-filter-${cat.toLowerCase()}`}
                      onClick={() => setFilterCategory(cat)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        filterCategory === cat
                          ? 'bg-[#D4AF37] text-[#020120]'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {cat === 'All' || cat === 'Other' ? cat : (CATEGORY_LABELS[cat] || cat)} <span className="opacity-60">({count})</span>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {assets.filter(a => filterCategory === 'All' || (a.category || 'Other') === filterCategory).map((asset) => {
                const isImage = asset.type?.startsWith('image/');
                const isVideo = asset.type?.startsWith('video/');
                
                return (
                  <div
                    key={asset.name}
                    className="bg-white/5 border border-white/10 rounded-xl overflow-hidden group"
                  >
                    <div className="aspect-video bg-white/5 flex items-center justify-center relative overflow-hidden">
                      {isImage ? (
                        <img 
                          src={asset.url} 
                          alt={asset.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : isVideo ? (
                        <video src={asset.url} className="w-full h-full object-cover" />
                      ) : (
                        <FileText className="w-12 h-12 text-white/30" />
                      )}
                      {/* Hover actions */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <a
                          href={asset.url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 bg-white/20 rounded-lg hover:bg-white/40 transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5 text-white" />
                        </a>
                        <button
                          onClick={() => handleDeleteAsset(asset)}
                          data-testid={`media-delete-${asset.displayName || asset.name}`}
                          className="p-2 bg-red-500/50 rounded-lg hover:bg-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-white text-sm truncate" title={asset.name}>{asset.displayName || asset.name}</p>
                      <p className="text-white/50 text-xs mt-1">{formatSize(asset.size)}{asset.category && asset.category !== 'Other' ? ` · ${asset.category}` : ''}</p>
                    </div>
                  </div>
                );
              })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Privacy Policy Manager
const PrivacyManager = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  const fetchPolicy = async () => {
    try {
      const res = await fetch(`${API_URL}/api/privacy`);
      if (res.ok) {
        const data = await res.json();
        setPolicy(data);
      }
    } catch (err) {
      toast.error('Failed to load privacy policy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPolicy(); }, []);

  const savePolicy = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/privacy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(policy)
      });
      if (res.ok) {
        toast.success('Privacy policy updated!');
        setEditingSection(null);
        fetchPolicy();
      } else {
        toast.error('Failed to save');
      }
    } catch (err) {
      toast.error('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (sectionId, field, value) => {
    setPolicy(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    }));
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      content: 'Enter content here...'
    };
    setPolicy(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setEditingSection(newSection.id);
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    setPolicy(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  if (loading) return <div className="text-white/60 text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="privacy-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white font-playfair">Privacy Policy</h2>
          <p className="text-white/50 text-sm mt-1">
            Last updated: {policy?.lastUpdated ? new Date(policy.lastUpdated).toLocaleDateString('en-GB') : 'Never'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={addSection}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Section
          </button>
          <button
            onClick={savePolicy}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960C] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {policy?.sections?.map((section, index) => (
          <div
            key={section.id}
            className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
          >
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5"
              onClick={() => setEditingSection(editingSection === section.id ? null : section.id)}
            >
              <div className="flex items-center gap-3">
                <span className="text-white/30 text-sm font-mono">#{index + 1}</span>
                <h3 className="text-white font-semibold">{section.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSection(section.id); }}
                  className="p-2 text-white/30 hover:text-red-400 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className={`w-5 h-5 text-white/50 transition-transform ${editingSection === section.id ? 'rotate-90' : ''}`} />
              </div>
            </div>

            <AnimatePresence>
              {editingSection === section.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-white/10"
                >
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Section Title</label>
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">
                        Content <span className="text-white/30">(Use **text** for bold)</span>
                      </label>
                      <textarea
                        value={section.content}
                        onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                        rows={10}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white font-mono text-sm"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <p className="text-blue-400 text-sm">
          <strong>Tip:</strong> Use **double asterisks** around text to make it bold. Use line breaks to create paragraphs.
        </p>
      </div>
    </div>
  );
};

// Settings Manager
const SettingsManager = ({ settings, onUpdate }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white font-playfair">Site Settings</h2>
      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
        <p className="text-white/60 text-center">Site settings editor coming soon</p>
      </div>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shows, setShows] = useState([]);
  const [tourDates, setTourDates] = useState([]);
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Hide the reCAPTCHA badge inside the admin (it overlays table action buttons)
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = '.grecaptcha-badge { visibility: hidden !important; }';
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [showsRes, tourDatesRes, newsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/shows`),
        fetch(`${API_URL}/api/tour-dates?includePast=true`),
        fetch(`${API_URL}/api/news`),
        fetch(`${API_URL}/api/settings`)
      ]);

      if (showsRes.ok) setShows(await showsRes.json());
      if (tourDatesRes.ok) setTourDates(await tourDatesRes.json());
      if (newsRes.ok) setNews(await newsRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview shows={shows} tourDates={tourDates} news={news} />;
      case 'shows':
        return <ShowsManager shows={shows} onUpdate={fetchData} />;
      case 'tour-dates':
        return <TourDatesManager tourDates={tourDates} shows={shows} onUpdate={fetchData} />;
      case 'media':
        return <MediaManager shows={shows} />;
      case 'about':
        return <AboutManager />;
      case 'news':
        return <NewsManager news={news} onUpdate={fetchData} />;
      case 'history':
        return <HistoryManager onUpdate={fetchData} />;
      case 'privacy':
        return <PrivacyManager />;
      case 'press-codes':
        return <PressCodesManager />;
      case 'settings':
        return <SettingsManager settings={settings} onUpdate={fetchData} />;
      default:
        return <DashboardOverview shows={shows} tourDates={tourDates} news={news} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08065E] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/60">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#08065E] via-[#0a0875] to-[#08065E] flex">
      {/* Sidebar */}
      <SidebarNav 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-[#08065E]/80 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-white/70 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex-1 lg:flex-none"></div>

          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
            >
              <Eye className="w-4 h-4" />
              View Site
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6 overflow-y-auto">
          {renderSection()}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
